import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useOrg } from "@/hooks/useOrg";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import type { Tables } from "@/integrations/supabase/types";

interface OrgMember {
  id: string;
  user_id: string | null;
  role: string;
  email: string | null;
}

export default function Dashboard() {
  const { orgId } = useOrg();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: members = [] } = useQuery<OrgMember[]>({
    queryKey: ["org_members", orgId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_org_members", { _org_id: orgId! });
      if (error) throw error;
      return data as OrgMember[];
    },
    enabled: !!orgId,
  });

  const { data: subInfo } = useQuery({
    queryKey: ["subscription", orgId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        body: { orgId },
      });
      if (error) throw error;
      return data as { subscribed: boolean; tier: string };
    },
    enabled: !!orgId,
    refetchInterval: 60_000,
  });

  const isOwner = members.some((m) => m.user_id === user?.id && m.role === "owner");
  const isPro = subInfo?.tier === "pro";
  const ownerMember = members.find((m) => m.role === "owner");

  // Non-owners on an inactive subscription see a blocked state
  if (subInfo && !isPro && !isOwner) {
    return (
      <div className="flex h-full items-center justify-center p-4 sm:p-8">
        <div className="max-w-sm text-center space-y-3">
          <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
          <h1 className="text-lg font-semibold text-foreground">Subscription inactive</h1>
          <p className="text-sm text-muted-foreground">
            Your team's subscription is no longer active. Ask the team owner
            {ownerMember?.email ? (
              <> (<a href={`mailto:${ownerMember.email}`} className="underline text-foreground">{ownerMember.email}</a>)</>
            ) : null}
            {" "}to upgrade to regain access.
          </p>
        </div>
      </div>
    );
  }

  return <DashboardContent orgId={orgId} />;
}

function DashboardContent({ orgId }: { orgId: string | null }) {
  const queryClient = useQueryClient();

  const { data: banners = [], isLoading } = useQuery({
    queryKey: ["banners", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("banners")
        .select("*")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from("banners").update({ is_active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["banners"] }),
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("banners").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["banners"] });
      toast.success("Banner deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Banners</h1>
          <p className="text-sm text-muted-foreground">Manage your status banners</p>
        </div>
        <Button asChild size="sm">
          <Link to="/dashboard/new">
            <Plus className="h-4 w-4" />
            New banner
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : banners.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16">
          <p className="text-sm text-muted-foreground">No banners yet</p>
          <Button asChild variant="ghost" size="sm" className="mt-2">
            <Link to="/dashboard/new">Create your first banner</Link>
          </Button>
        </div>
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border">
          {banners.map((banner) => (
            <BannerRow
              key={banner.id}
              banner={banner}
              onToggle={(active) => toggleMutation.mutate({ id: banner.id, is_active: active })}
              onDelete={() => deleteMutation.mutate(banner.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
function BannerRow({
  banner,
  onToggle,
  onDelete,
}: {
  banner: Tables<"banners">;
  onToggle: (active: boolean) => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-center gap-4 px-4 py-3">
      <Switch checked={banner.is_active} onCheckedChange={onToggle} />

      <div className="flex-1 min-w-0">
        <p className="truncate text-sm font-medium text-foreground">{banner.body || "Untitled banner"}</p>
        <p className="text-xs text-muted-foreground">
          Updated {formatDistanceToNow(new Date(banner.updated_at), { addSuffix: true })}
        </p>
      </div>

      {/* <div
        className="hidden sm:block h-6 w-32 rounded"
        style={{ backgroundColor: banner.bg_color }}
      /> */}

      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/dashboard/${banner.id}`}>
            <Pencil className="h-3.5 w-3.5" />
          </Link>
        </Button>
        <Button variant="ghost" size="icon" onClick={onDelete}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
